import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as templatesApi from '../api/templates';
import * as eventOpsApi from '../api/event-ops';
import * as questionnairesApi from '../api/questionnaires';
import type { TemplateItem } from '../api/templates';

export type TemplateType = 'timeline' | 'checklist' | 'kibuddim' | 'questionnaire';

export function useTemplates(type?: string) {
  return useQuery<TemplateItem[]>({
    queryKey: ['templates', type ?? 'all'],
    queryFn: () => templatesApi.listTemplates(type),
  });
}

export function useSaveAsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, templateName, type }: { id: string; templateName: string; type: string }) =>
      templatesApi.saveAsTemplate(id, templateName, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function useArchiveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      templatesApi.archiveTemplate(id, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

interface CreateTemplateInput {
  type: TemplateType;
  templateName: string;
  title?: string;
  description?: string;
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, CreateTemplateInput>({
    mutationFn: ({ type, templateName, title, description }) => {
      const payload = {
        isTemplate: true,
        templateName,
        title: title || templateName,
        description: description || undefined,
      };
      if (type === 'timeline') return eventOpsApi.createTimeline(payload);
      if (type === 'checklist') return eventOpsApi.createChecklist(payload);
      if (type === 'questionnaire') {
        return questionnairesApi.createQuestionnaire({
          isTemplate: true,
          templateName,
          title: title || templateName,
          description: description || undefined,
        });
      }
      return eventOpsApi.createKibuddim(payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      const listKey = variables.type === 'kibuddim' ? 'kibuddim' : `${variables.type}s`;
      queryClient.invalidateQueries({ queryKey: [listKey] });
    },
  });
}

interface UseTemplateInput {
  templateId: string;
  type: TemplateType;
  eventId: string;
  title?: string;
}

export function useCloneTemplateToEvent() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, UseTemplateInput>({
    mutationFn: ({ templateId, type, eventId, title }) => {
      const body = { eventId, title };
      if (type === 'timeline') return eventOpsApi.cloneTimeline(templateId, body);
      if (type === 'checklist') return eventOpsApi.cloneChecklist(templateId, body);
      if (type === 'questionnaire') return questionnairesApi.cloneQuestionnaire(templateId, body);
      return eventOpsApi.cloneKibuddim(templateId, body);
    },
    onSuccess: (_data, variables) => {
      const listKey = variables.type === 'kibuddim' ? 'kibuddim' : `${variables.type}s`;
      queryClient.invalidateQueries({ queryKey: [listKey] });
      if (variables.type === 'questionnaire') {
        // Cloning a questionnaire may auto-provision a timeline and event tags.
        queryClient.invalidateQueries({ queryKey: ['timelines'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
      }
    },
  });
}

interface CreateBlankInput {
  type: TemplateType;
  eventId: string;
  title: string;
  description?: string;
}

interface UpdateTemplateInput {
  id: string;
  type: TemplateType;
  templateName?: string;
  description?: string | null;
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, UpdateTemplateInput>({
    mutationFn: ({ id, type, templateName, description }) => {
      const payload = { templateName, description };
      if (type === 'timeline') return eventOpsApi.updateTimeline(id, payload);
      if (type === 'checklist') return eventOpsApi.updateChecklist(id, payload);
      if (type === 'questionnaire') return questionnairesApi.updateQuestionnaire(id, payload);
      return eventOpsApi.updateKibuddim(id, payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      const listKey = variables.type === 'kibuddim' ? 'kibuddim' : `${variables.type}s`;
      queryClient.invalidateQueries({ queryKey: [listKey] });
    },
  });
}

export function useCreateBlankForEvent() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, CreateBlankInput>({
    mutationFn: ({ type, eventId, title, description }) => {
      const payload = { eventId, title, description: description || undefined };
      if (type === 'timeline') return eventOpsApi.createTimeline(payload);
      if (type === 'checklist') return eventOpsApi.createChecklist(payload);
      return eventOpsApi.createKibuddim(payload);
    },
    onSuccess: (_data, variables) => {
      const listKey = variables.type === 'kibuddim' ? 'kibuddim' : `${variables.type}s`;
      queryClient.invalidateQueries({ queryKey: [listKey] });
    },
  });
}
